/**
 * Sign-in page.
 *
 * Prototype credentials go nowhere near a URL: the form submits in memory to the
 * mock auth service. On success the visitor is returned to wherever the auth gate
 * sent them from, or to the dashboard.
 */

import { el, icon, fieldValue } from "../ui/dom.js";
import { pageHeader, errorAlert, successAlert } from "../ui/patterns.js";

/** @type {import("../app/router.js").PageRenderer} */
export default function renderSignIn(context) {
  const frag = document.createDocumentFragment();
  frag.append(pageHeader("Sign in"));

  const errorRegion = el("div", { "aria-live": "assertive" });

  const emailField = el("wa-input", {
    label: "Email address",
    name: "email",
    type: "email",
    autocomplete: "email",
    required: true,
  });

  const passwordField = el("wa-input", {
    label: "Password",
    name: "password",
    type: "password",
    autocomplete: "current-password",
    "password-toggle": true,
    required: true,
  });

  const submit = el("wa-button", { type: "submit", variant: "brand" }, [
    icon("right-to-bracket"),
    "Sign in",
  ]);

  const form = el(
    "form",
    {
      class: "kin-stack kin-form",
      novalidate: true,
      on: {
        submit: async (event) => {
          event.preventDefault();
          errorRegion.replaceChildren();
          submit.setAttribute("loading", "");
          try {
            await context.services.auth.signIn(fieldValue(emailField), fieldValue(passwordField));
            const returnTo =
              typeof context.state.returnTo === "string" ? context.state.returnTo : "/dashboard";
            context.navigate(returnTo);
          } catch (error) {
            submit.removeAttribute("loading");
            errorRegion.replaceChildren(
              errorAlert(error instanceof Error ? error.message : "Sign in failed."),
            );
          }
        },
      },
    },
    [emailField, passwordField, submit],
  );

  // Password reset. Reuses the email already typed above rather than asking for
  // it twice. The confirmation is deliberately worded so it says the same thing
  // whether or not the address has an account — the service never discloses that,
  // so the UI must not either (§16, account enumeration).
  const resetButton = el(
    "wa-button",
    {
      appearance: "plain",
      on: {
        click: async () => {
          errorRegion.replaceChildren();
          const email = fieldValue(emailField);
          if (!email) {
            errorRegion.replaceChildren(
              errorAlert("Enter your email address above, then choose reset again."),
            );
            emailField.focus();
            return;
          }
          resetButton.setAttribute("loading", "");
          try {
            await context.services.auth.sendPasswordReset(email);
            errorRegion.replaceChildren(
              successAlert(
                "If that email address has an account, we have sent a link to reset the password.",
              ),
            );
          } catch (error) {
            errorRegion.replaceChildren(
              errorAlert(
                error instanceof Error ? error.message : "Could not send a reset email just now.",
              ),
            );
          } finally {
            resetButton.removeAttribute("loading");
          }
        },
      },
    },
    [icon("envelope"), "Forgotten your password?"],
  );

  frag.append(
    errorRegion,
    form,
    el("p", { class: "kin-note" }, [resetButton]),
    el("p", { class: "kin-note" }, [
      "New to KinCode? ",
      el("a", { href: "/register", dataset: { link: "" } }, ["Create an account"]),
      ".",
    ]),
  );
  return frag;
}
